/**
 * Pinterest Advertising Error Parser (Story 19.4, Task 2)
 *
 * Maps Pinterest API v5 error responses to standardized error types.
 *
 * Pinterest surfaces errors in a single shape (REST envelope, no SOAP):
 *   { "code": <int>, "message": "...", "status": "failure" }
 *
 * The parser inspects HTTP status first (the cheapest signal) and only falls
 * back to body-shape sniffing when the status isn't in our table.
 *
 * @see https://developers.pinterest.com/docs/api/v5/error-codes/
 */

// ============================================================================
// Types
// ============================================================================

export type PinterestAdsErrorType =
  | "token_expired"
  | "permission_denied"
  | "not_found"
  | "rate_limited"
  | "provider_unavailable"
  | "unknown";

export interface PinterestAdsApiError {
  type: PinterestAdsErrorType;
  message: string;
  status: number | null;
  retryable: boolean;
}

interface RestErrorEnvelope {
  /** Pinterest's numeric error code (varies per endpoint) */
  code?: number | string;
  /** Pinterest's user-facing error message */
  message?: string;
  /** Pinterest's status string ("failure" on errors) */
  status?: string;
  /** Standard OAuth 2.0 error fallback (rare on data endpoints) */
  error?: string;
  error_description?: string;
}

// ============================================================================
// Status Code Mapping
// ============================================================================

const STATUS_CODE_MAP: Record<
  number,
  { type: PinterestAdsErrorType; retryable: boolean }
> = {
  401: { type: "token_expired", retryable: false },
  403: { type: "permission_denied", retryable: false },
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limited", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

// ============================================================================
// Error Parser
// ============================================================================

/**
 * Parses a Pinterest Ads error response into a standardized error object.
 *
 * Resolution order:
 *   1. HTTP status (fastest, most reliable)
 *   2. REST envelope `message` (or `error_description` for OAuth-style errors)
 *   3. Fallback to "unknown"
 *
 * @returns The mapped error, or null when the input is empty/successful (HTTP 2xx
 *          with no error body).
 */
export function parsePinterestAdsApiError(
  responseBody: unknown,
  httpStatus?: number
): PinterestAdsApiError | null {
  const body = (responseBody ?? {}) as RestErrorEnvelope;

  // ---- 1. HTTP status mapping ------------------------------------------------
  if (httpStatus && (httpStatus < 200 || httpStatus >= 300)) {
    const mapping = STATUS_CODE_MAP[httpStatus];
    if (mapping) {
      const message = extractErrorMessage(body, httpStatus);
      return {
        type: mapping.type,
        message,
        status: httpStatus,
        retryable: mapping.retryable,
      };
    }
  }

  // ---- 2. REST envelope without explicit code mapping ------------------------
  if (body.message) {
    return {
      type: "unknown",
      message: body.message,
      status: httpStatus ?? null,
      retryable: false,
    };
  }
  if (body.error_description) {
    return {
      type: "unknown",
      message: body.error_description,
      status: httpStatus ?? null,
      retryable: false,
    };
  }

  // ---- 3. No identifiable error -----------------------------------------------
  if (!httpStatus || (httpStatus >= 200 && httpStatus < 300)) {
    return null;
  }

  return {
    type: "unknown",
    message: extractErrorMessage(body, httpStatus),
    status: httpStatus,
    retryable: false,
  };
}

// ============================================================================
// Typed error class
// ============================================================================

/**
 * Thrown by `fetchPinterestAdsUserAccount` / `fetchPinterestAdsAdAccounts`
 * when Pinterest returns a non-2xx response. Carries the parsed error type so
 * the adapter can route it to the correct `AdapterErrorCode` without
 * string-matching the message (mirrors MS Ads review #2 M3).
 */
export class PinterestAdsApiCallError extends Error {
  readonly type: PinterestAdsErrorType;
  readonly status: number | null;
  readonly retryable: boolean;
  constructor(parsed: PinterestAdsApiError, prefix: string) {
    super(`${prefix}: ${parsed.message}`);
    this.name = "PinterestAdsApiCallError";
    this.type = parsed.type;
    this.status = parsed.status;
    this.retryable = parsed.retryable;
  }
}

/**
 * Maps a Pinterest Ads error type to the corresponding `AdapterErrorCode`.
 * Used by the adapter so we don't string-match localised provider messages.
 */
export function pinterestAdsErrorTypeToAdapterCode(
  type: PinterestAdsErrorType
): "token_expired" | "permission_denied" | "rate_limited" | "provider_unavailable" | "invalid_response" | "account_not_found" {
  switch (type) {
    case "token_expired":
      return "token_expired";
    case "permission_denied":
      return "permission_denied";
    case "not_found":
      // Pinterest 404 can mean "no accounts visible to this token" (benign
      // onboarding state) OR "endpoint URL changed" (post-deploy schema drift).
      // We prefer `invalid_response` over `account_not_found` because callers
      // already explicitly check for empty `items` arrays for the benign case
      // before throwing — any 404 surfacing here is more likely a deploy issue.
      // Matches MS Ads review #3 M2.
      return "invalid_response";
    case "rate_limited":
      return "rate_limited";
    case "provider_unavailable":
      return "provider_unavailable";
    case "unknown":
    default:
      return "invalid_response";
  }
}

/**
 * Pulls the most descriptive message we can find out of a Pinterest error
 * envelope, with a sensible fallback when nothing structured is present.
 */
function extractErrorMessage(body: RestErrorEnvelope, status: number): string {
  if (body.message) return body.message;
  if (body.error_description) return body.error_description;
  if (body.error) return body.error;
  return `Pinterest Ads API error: HTTP ${status}`;
}
